<template>
  <v-row>
    <v-col cols="12">
      <v-card class="pur-card" flat>
        <!-- HEADER BAND -->
        <div class="pur-header pl-base-bg">
          <div class="pur-header-icon">
            <v-icon color="#fff" size="22">mdi-cart-plus</v-icon>
          </div>
          <div>
            <div class="pur-title">Record Purchase</div>
            <div class="pur-subtitle">Capture a vendor bill for GST/ITC tracking</div>
          </div>
        </div>

        <v-form ref="form" v-model="valid" class="pa-6">
          <!-- VENDOR + BILL DETAILS -->
          <div class="pur-section-label">Bill Details</div>
          <v-row dense>
            <v-col cols="12" sm="4">
              <v-autocomplete
                v-model="selectedVendor"
                :items="vendors"
                item-text="name"
                return-object
                label="Vendor"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-domain"
                @change="onVendorSelect"
              >
                <template v-slot:append>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        v-bind="attrs"
                        v-on="on"
                        color="primary"
                        style="cursor: pointer"
                        @click="vendorDialog = true"
                      >
                        mdi-plus
                      </v-icon>
                    </template>
                    <span>New Vendor</span>
                  </v-tooltip>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12" sm="4">
              <v-text-field
                v-model="form.billNumber"
                label="Vendor Bill Number"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-file-document-outline"
              />
            </v-col>

            <v-col cols="12" sm="4">
              <v-menu
                v-model="dateMenu"
                :close-on-content-click="false"
                offset-y
                max-width="290"
                min-width="290"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.billDate"
                    label="Bill Date"
                    outlined
                    dense
                    readonly
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker v-model="form.billDate" @input="dateMenu = false" />
              </v-menu>
            </v-col>
          </v-row>

          <!-- ITEMS -->
          <div class="pur-section-label mt-4">Items</div>

          <div
            v-for="(item, index) in form.items"
            :key="index"
            class="pur-item-row"
          >
            <div class="pur-item-row-head">
              <span class="pur-item-index">Item {{ index + 1 }}</span>
              <v-btn icon x-small class="pur-item-remove" @click="removeItem(index)">
                <v-icon color="error" size="20">mdi-delete-outline</v-icon>
              </v-btn>
            </div>

            <v-row dense>
              <v-col cols="12" sm="5">
                <v-text-field
                  v-model="item.description"
                  label="Description"
                  dense
                  outlined
                  hide-details
                  :rules="[rules.required]"
                />
              </v-col>

              <v-col cols="6" sm="2">
                <v-text-field
                  v-model="item.hsn"
                  label="HSN/SAC"
                  dense
                  outlined
                  hide-details
                />
              </v-col>

              <v-col cols="4" sm="1">
                <v-text-field
                  v-model.number="item.quantity"
                  type="number"
                  label="Qty"
                  dense
                  outlined
                  hide-details
                  @input="calcItem(index)"
                />
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model.number="item.price"
                  type="number"
                  label="Rate"
                  dense
                  outlined
                  hide-details
                  @input="calcItem(index)"
                />
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model.number="item.taxPercent"
                  type="number"
                  label="Tax %"
                  dense
                  outlined
                  hide-details
                  suffix="%"
                  @input="calcItem(index)"
                />
              </v-col>
            </v-row>

            <div class="pur-item-row-foot">
              <div class="pur-item-amount">
                <span class="pur-item-amount-label">Amount</span>
                <span class="pur-item-amount-value">{{ formatAmount(item.amount) }}</span>
              </div>

              <div class="pur-item-itc" :class="{ 'pur-item-itc--off': !item.isItcEligible }">
                <v-switch
                  v-model="item.isItcEligible"
                  dense
                  inset
                  hide-details
                  class="pur-item-itc-switch"
                />
                <span>ITC Eligible</span>
              </div>
            </div>
          </div>

          <v-btn small class="pur-add-item-btn" @click="addItem">
            <v-icon left size="16">mdi-plus</v-icon>
            Add Item
          </v-btn>

          <!-- ITC NOTE -->
          <v-alert type="info" outlined dense border="left" class="mt-4 pur-itc-note">
            ITC Eligible is your own judgment call per item — GST law blocks or
            restricts input tax credit on certain categories (e.g. motor
            vehicles, employee benefits). Untick it for any line you know
            isn't eligible; check with your accountant if unsure.
          </v-alert>

          <!-- TOTALS -->
          <v-divider class="my-4" />
          <v-row dense justify="end">
            <v-col cols="12" sm="4">
              <div class="pur-total-row">
                <span>Subtotal</span>
                <span>{{ formatAmount(subtotal) }}</span>
              </div>
              <div class="pur-total-row">
                <span>GST</span>
                <span>{{ formatAmount(totalGst) }}</span>
              </div>
              <div class="pur-total-row pur-total-row--grand">
                <span>Total</span>
                <span>{{ formatAmount(subtotal + totalGst) }}</span>
              </div>
            </v-col>
          </v-row>

          <!-- BILL IMAGE UPLOAD -->
          <div class="pur-section-label mt-4">Bill / Receipt (optional)</div>
          <v-file-input
            v-model="uploadedFile"
            label="Attach a photo or PDF of the vendor bill"
            outlined
            dense
            prepend-icon=""
            prepend-inner-icon="mdi-paperclip"
            accept="image/*,application/pdf"
            @change="onFileSelected"
          />

          <!-- NOTE -->
          <v-textarea
            v-model="form.note"
            label="Note"
            outlined
            dense
            rows="2"
          />

          <!-- ACTIONS -->
          <v-row class="mt-4" justify="end" dense>
            <v-col cols="6" sm="auto">
              <v-btn block color="secondary" @click="reset">Reset</v-btn>
            </v-col>

            <v-col v-if="!isViewOnly" cols="6" sm="auto">
              <v-btn block class="pur-save-btn" :loading="loading" @click="submit">
                Save
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-col>

    <!-- INLINE ADD-VENDOR DIALOG -->
    <global-dialog v-if="vendorDialog" v-model="vendorDialog" title="New Vendor">
      <create-vendor mode="dialog" @created="onVendorCreated" />
    </global-dialog>
  </v-row>
</template>

<script>
import { mapActions } from "vuex";
import GlobalDialog from "@/components/common/global-dialog.vue";
import CreateVendor from "@/components/create-vendor.vue";

export default {
  name: "CreatePurchase",
  components: { GlobalDialog, CreateVendor },

  data() {
    return {
      valid: false,
      loading: false,
      dateMenu: false,
      vendorDialog: false,
      selectedVendor: null,
      uploadedFile: null,

      form: {
        billNumber: "",
        billDate: new Date().toISOString().slice(0, 10),
        reverseCharge: false,
        note: "",
        items: [
          {
            description: "",
            hsn: "",
            quantity: 1,
            price: 0,
            taxPercent: 18,
            amount: 0,
            isItcEligible: true,
          },
        ],
      },

      rules: {
        required: (v) => !!v || "Required",
      },
    };
  },

  computed: {
    vendors() {
      return this.$store.state.vendors.vendors;
    },

    subtotal() {
      return this.form.items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
    },

    totalGst() {
      return this.form.items.reduce((sum, it) => {
        const amount = Number(it.amount) || 0;
        const percent = Math.min(28, Math.max(0, Number(it.taxPercent) || 0));
        return sum + (amount * percent) / 100;
      }, 0);
    },
  },

  created() {
    this.$store.dispatch("vendors/getVendors");
  },

  methods: {
    ...mapActions("purchases", ["createPurchase"]),

    onVendorSelect(vendor) {
      this.selectedVendor = vendor;
    },

    onVendorCreated(vendor) {
      this.vendorDialog = false;
      this.selectedVendor = vendor;
    },

    addItem() {
      this.form.items.push({
        description: "",
        hsn: "",
        quantity: 1,
        price: 0,
        taxPercent: 18,
        amount: 0,
        isItcEligible: true,
      });
    },

    removeItem(i) {
      if (this.form.items.length === 1) return;
      this.form.items.splice(i, 1);
    },

    calcItem(i) {
      const item = this.form.items[i];
      if (!item) return;

      const qty = Number(item.quantity) || 0;
      const price = Number(item.price) || 0;
      item.amount = +(qty * price).toFixed(2);
    },

    formatAmount(value) {
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(value || 0);
    },

    onFileSelected(file) {
      this.uploadedFile = file;
    },

    readFileAsBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },

    async buildPayload() {
      const payload = {
        billNumber: this.form.billNumber,
        billDate: this.form.billDate,
        reverseCharge: this.form.reverseCharge,
        note: this.form.note,
        vendor: {
          vendorId: this.selectedVendor._id,
          name: this.selectedVendor.name,
          gstin: this.selectedVendor.gstin,
          address: this.selectedVendor.address,
          state: this.selectedVendor.state,
        },
        items: this.form.items,
      };

      if (this.uploadedFile) {
        payload.file = await this.readFileAsBase64(this.uploadedFile);
        payload.fileName = this.uploadedFile.name;
        payload.fileType = this.uploadedFile.type;
      }

      return payload;
    },

    async submit() {
      if (!this.$refs.form.validate()) return;
      if (!this.selectedVendor) {
        this.$toast?.error("Please select a vendor");
        return;
      }

      this.loading = true;

      try {
        const payload = await this.buildPayload();

        try {
          await this.createPurchase(payload);
        } catch (err) {
          if (err?.response?.data?.code !== "DUPLICATE_PURCHASE") throw err;

          const proceed = window.confirm(`${err.response.data.message}. Save anyway?`);
          if (!proceed) return;

          await this.createPurchase({ ...payload, confirmDuplicate: true });
        }

        this.$toast?.success("Purchase recorded successfully");
        this.$router.push("/purchases");
      } catch (err) {
        this.$toast?.error(
          err?.response?.data?.message || "Failed to record purchase",
        );
      } finally {
        this.loading = false;
      }
    },

    reset() {
      this.$refs.form.reset();
      this.selectedVendor = null;
      this.uploadedFile = null;
      this.form.items = [
        {
          description: "",
          hsn: "",
          quantity: 1,
          price: 0,
          taxPercent: 18,
          amount: 0,
          isItcEligible: true,
        },
      ];
    },
  },
};
</script>

<style scoped>
.pur-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.pur-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.pur-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pur-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.pur-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.pur-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.pur-item-row {
  background: #f8fafc;
  border: 1px solid #eef1f5;
  border-radius: 0;
  padding: 12px 14px 14px;
  margin-bottom: 12px;
}

.pur-item-row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.pur-item-index {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
}

.pur-item-remove {
  margin: -4px -4px 0 0;
}

.pur-item-row-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #e2e8f0;
}

.pur-item-amount {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.pur-item-amount-label {
  font-size: 12px;
  color: #64748b;
}

.pur-item-amount-value {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.pur-item-itc {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #15803d;
  background: #e6f7ec;
  border-radius: 100px;
  padding: 4px 12px 4px 4px;
  transition: background 0.15s, color 0.15s;
}

.pur-item-itc--off {
  color: #94a3b8;
  background: #eef1f5;
}

.pur-item-itc-switch {
  margin: 0 !important;
  padding: 0 !important;
}

.pur-item-itc-switch >>> .v-input__control,
.pur-item-itc-switch >>> .v-input__slot {
  margin: 0 !important;
}

.pur-item-itc-switch >>> .v-messages {
  display: none;
}

.pur-add-item-btn {
  background: #eef3fb !important;
  color: #0a5a90 !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.pur-itc-note {
  font-size: 13px;
}

.pur-total-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 4px 0;
  color: #475569;
}

.pur-total-row--grand {
  font-weight: 700;
  font-size: 16px;
  color: #0f172a;
  border-top: 1px solid #eef1f5;
  margin-top: 4px;
  padding-top: 8px;
}

.pur-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
</style>
